import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { LEAD_STATUSES, formatEUR, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Flame, Plus, Search, ChevronRight, Sparkles, Boxes } from "lucide-react";
import { SolutionBadge, PriorityBadge, EstimateStatusBadge, HotLeadBadge } from "@/components/EnergyBadges";

const PRIORITY_RANK = { urgent: 0, high: 1, normal: 2, low: 3 };
const TEMP_RANK = { hot: 0, warm: 1, info: 2, cold: 3, "": 3 };

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(() => searchParams.get("status") || "alle");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      const r = await api.get("/leads", { params: filter !== "alle" ? { status: filter } : {} });
      setLeads(r.data);
      setLoading(false);
    })();
  }, [filter]);

  const filtered = useMemo(() => {
    const out = leads.filter((l) => {
      const t = `${l.first_name} ${l.last_name} ${l.email} ${l.address}`.toLowerCase();
      return t.includes(search.toLowerCase());
    });
    // Energie-Erweiterung Phase E — Hot/Urgent zuerst (Bestandssortierung created_at-DESC bleibt darunter).
    out.sort((a, b) => {
      const pa = PRIORITY_RANK[a.priority || "normal"] ?? 2;
      const pb = PRIORITY_RANK[b.priority || "normal"] ?? 2;
      if (pa !== pb) return pa - pb;
      const ta = TEMP_RANK[a.lead_temperature || ""] ?? 3;
      const tb = TEMP_RANK[b.lead_temperature || ""] ?? 3;
      if (ta !== tb) return ta - tb;
      return 0;
    });
    return out;
  }, [leads, search]);

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Leads
          </h1>
          <p className="mt-1 text-slate-600">Alle eingehenden PV-Anfragen im Überblick</p>
        </div>
        <Button data-testid="create-lead-btn" onClick={() => nav("/leads/new")} className="bg-orange-600 hover:bg-orange-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> Neuer Lead
        </Button>
        <Button data-testid="create-lead-ai-btn" onClick={() => nav("/leads/new-ai")} variant="outline" className="border-slate-300">
          <Sparkles className="w-4 h-4 mr-2" /> KI-Chat
        </Button>
      </div>

      <Card className="border-slate-200 p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="leads-search"
            placeholder="Suche nach Name, E-Mail, Adresse …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="leads-status-filter" className="w-[220px]">
            <SelectValue placeholder="Status filtern" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Status</SelectItem>
            {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr className="text-left">
                <th className="px-6 py-3 font-semibold">Kunde</th>
                <th className="px-6 py-3 font-semibold">Adresse</th>
                <th className="px-6 py-3 font-semibold text-right">Wert</th>
                <th className="px-6 py-3 font-semibold">Status</th>
                <th className="px-6 py-3 font-semibold">Letzter Kontakt</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan="6" className="px-6 py-10 text-center text-slate-500">Wird geladen …</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-10 text-center text-slate-500">Keine Leads gefunden.</td></tr>
              ) : (
                filtered.map((l) => (
                  <tr
                    key={l.lead_id}
                    data-testid={`lead-row-${l.lead_id}`}
                    onClick={() => nav(`/leads/${l.lead_id}`)}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {l.is_hot && <Flame className="w-4 h-4 text-orange-500" />}
                        <div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-medium text-slate-900">{l.first_name} {l.last_name}</p>
                            {l.source === "configurator" && (
                              <span data-testid={`source-badge-${l.lead_id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-50 border border-orange-200 text-orange-700 text-[10px] font-semibold uppercase tracking-wider">
                                <Boxes className="w-2.5 h-2.5" /> Konfigurator
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap mt-1">
                            <SolutionBadge value={l.solution_type} testid={`row-solution-${l.lead_id}`} />
                            <PriorityBadge value={l.priority} testid={`row-priority-${l.lead_id}`} />
                            {l.estimate_status && <EstimateStatusBadge value={l.estimate_status} testid={`row-estimate-${l.lead_id}`} />}
                            {l.is_hot && <HotLeadBadge reason={l.hot_lead_reason} testid={`row-hot-${l.lead_id}`} />}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{l.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{l.address || "—"}</td>
                    <td className="px-6 py-4 text-right tabular-nums text-slate-700">{formatEUR(l.estimated_value)}</td>
                    <td className="px-6 py-4"><StatusBadge status={l.status} /></td>
                    <td className="px-6 py-4 text-sm text-slate-600">{formatDate(l.last_contact_at)}</td>
                    <td className="px-6 py-4 text-right text-slate-400"><ChevronRight className="w-4 h-4 inline" /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-slate-100">
          {loading ? (
            <p className="px-4 py-8 text-center text-slate-500 text-sm">Wird geladen …</p>
          ) : filtered.length === 0 ? (
            <p className="px-4 py-8 text-center text-slate-500 text-sm">Keine Leads gefunden.</p>
          ) : filtered.map((l) => (
            <button
              key={l.lead_id}
              data-testid={`lead-row-mobile-${l.lead_id}`}
              onClick={() => nav(`/leads/${l.lead_id}`)}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 active:bg-slate-100 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <div className="flex items-center gap-1.5 min-w-0">
                  {l.is_hot && <Flame className="w-4 h-4 text-orange-500 flex-shrink-0" />}
                  <p className="font-semibold text-slate-900 truncate">{l.first_name} {l.last_name}</p>
                </div>
                <span className="text-sm font-semibold tabular-nums whitespace-nowrap">{formatEUR(l.estimated_value)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <StatusBadge status={l.status} />
                <span className="text-xs text-slate-500">{formatDate(l.last_contact_at)}</span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                <SolutionBadge value={l.solution_type} testid={`row-mobile-solution-${l.lead_id}`} />
                <PriorityBadge value={l.priority} testid={`row-mobile-priority-${l.lead_id}`} />
                {l.estimate_status && <EstimateStatusBadge value={l.estimate_status} testid={`row-mobile-estimate-${l.lead_id}`} />}
                {l.is_hot && <HotLeadBadge reason={l.hot_lead_reason} testid={`row-mobile-hot-${l.lead_id}`} />}
              </div>
              {l.address && <p className="text-xs text-slate-500 mt-1.5 truncate">{l.address}</p>}
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
